import React, { useRef, useState } from 'react';
import { MAX_MESSAGE_LENGTH } from '../../domain/conversations';
import { DomainError } from '../../domain/errors';
import { ActionError } from '../PageStates';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ImagePlus, Send, X } from 'lucide-react';
import { ErrorText } from '../ErrorText';

/** How a send ended; a `DomainError` is a refusal to show (verify first). */
export type SendOutcome = 'sent' | 'refused' | 'failed' | DomainError;

/**
 * Writing a message: text up to 3,000 characters, one photo, Send. The text
 * clears at once (the message shows as sending); if the server refuses —
 * the conversation went read-only — it comes back so nothing is lost.
 */
export default function MessageComposer({
  onSend,
}: {
  onSend: (body: string, photo: File | null) => Promise<SendOutcome>;
}) {
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [sendingPhoto, setSendingPhoto] = useState(false);
  const [refusal, setRefusal] = useState<DomainError | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const body = text.trim();
  const tooLong = body.length > MAX_MESSAGE_LENGTH;
  const canSend = (body.length > 0 || photo !== null) && !tooLong && !sendingPhoto;

  const send = async () => {
    if (!canSend) return;
    const sentText = text;
    const sentPhoto = photo;
    setText('');
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = '';
    if (sentPhoto) setSendingPhoto(true);
    setRefusal(null);
    try {
      const outcome = await onSend(body, sentPhoto);
      if (outcome instanceof DomainError) setRefusal(outcome);
      if (outcome === 'refused' || outcome instanceof DomainError) {
        setText(sentText);
        setPhoto(sentPhoto);
      }
    } finally {
      setSendingPhoto(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="space-y-2 rounded-3xl bg-card p-3">
      {refusal && <ActionError error={refusal} />}
      {photo && (
        <div className="flex items-center gap-2 rounded-2xl bg-muted px-3 py-2 text-sm">
          <ImagePlus className="size-4 text-wood" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate">{photo.name}</span>
          <button
            type="button"
            aria-label="Remove photo"
            className="rounded-full p-1 hover:bg-secondary"
            onClick={() => {
              setPhoto(null);
              if (fileRef.current) fileRef.current.value = '';
            }}
          >
            <X className="size-4" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          aria-label="Photo to attach"
          data-testid="message-photo-input"
          onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label="Attach a photo"
          disabled={sendingPhoto}
          onClick={() => fileRef.current?.click()}
        >
          <ImagePlus className="size-5" />
        </Button>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Write a message…"
          aria-label="Message"
          aria-invalid={tooLong}
          rows={1}
          className="min-h-10 flex-1 resize-none rounded-2xl"
        />
        <Button
          type="button"
          size="icon"
          className="rounded-full"
          aria-label="Send"
          disabled={!canSend}
          onClick={send}
        >
          <Send className="size-4" />
        </Button>
      </div>
      {tooLong && (
        <ErrorText role="alert" className="px-2 text-xs">
          Messages can be up to {MAX_MESSAGE_LENGTH.toLocaleString('en-US')} characters.
        </ErrorText>
      )}
    </div>
  );
}
